import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { formatApiError } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, Shield, Save, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    name: '', clinic_name: '', phone: '', mci_registration_number: '',
    clinic_address: '', specialization: '', experience_years: 0,
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        clinic_name: user.clinic_name || '',
        phone: user.phone || '',
        mci_registration_number: user.mci_registration_number || '',
        clinic_address: user.clinic_address || '',
        specialization: user.specialization || '',
        experience_years: user.experience_years || 0,
      });
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSaved(false);
    try {
      await api.put('/auth/profile', form);
      await refreshUser();
      setSaved(true);
      toast.success('Profile updated successfully');
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const update = (key, val) => setForm({ ...form, [key]: val });

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-heading font-medium tracking-tight text-stone-900">Doctor Profile</h1>
        <p className="text-stone-500 mt-1">Manage your professional details and contact information</p>
      </div>

      {/* Verification notice */}
      <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg text-sm flex items-start gap-3">
        <Shield className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          <span className="font-semibold">Registration Verification</span> — Your Medical Council Registration Number (MCI/NMC) will be verified. Providing incorrect details may result in account suspension.
        </div>
      </div>

      <form onSubmit={handleSave}>
        {/* Personal Info */}
        <Card className="border-stone-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <User className="w-5 h-5 text-[#1A4D2E]" /> Personal Information
            </CardTitle>
            <CardDescription>Your basic details and verification status</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-stone-50 border border-stone-100">
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Email</p>
                <p className="text-sm font-medium text-stone-900">{user?.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={user?.is_verified ? 'bg-[#1A4D2E] text-white' : 'bg-amber-100 text-amber-800'}>
                  {user?.is_verified ? 'Verified' : 'Pending Verification'}
                </Badge>
                {user?.is_blocked && <Badge className="bg-[#8B2626] text-white">Blocked</Badge>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-stone-700">Full Name *</Label>
                <Input data-testid="profile-name" id="name" value={form.name} onChange={e => update('name', e.target.value)} placeholder="Dr. John Smith" required className="mt-1" />
              </div>
              <div>
                <Label htmlFor="phone" className="text-stone-700">Phone Number</Label>
                <Input data-testid="profile-phone" id="phone" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+91 9876543210" className="mt-1" />
              </div>
            </div>

            <Separator />

            <div>
              <Label htmlFor="mci" className="text-stone-700 font-semibold">Medical Council Registration Number (MCI/NMC) *</Label>
              <p className="text-xs text-stone-400 mb-1.5">This will be verified. Incorrect registration numbers will lead to account blocking.</p>
              <Input data-testid="profile-mci" id="mci" value={form.mci_registration_number} onChange={e => update('mci_registration_number', e.target.value)} placeholder="e.g., MCI-12345 or NMC-67890" className="mt-1 font-mono" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-700">Specialization</Label>
                <Select value={form.specialization} onValueChange={v => update('specialization', v)}>
                  <SelectTrigger data-testid="profile-specialization" className="mt-1">
                    <SelectValue placeholder="Select specialization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General Practice">General Practice</SelectItem>
                    <SelectItem value="Internal Medicine">Internal Medicine</SelectItem>
                    <SelectItem value="Pulmonology">Pulmonology</SelectItem>
                    <SelectItem value="Radiology">Radiology</SelectItem>
                    <SelectItem value="Cardiology">Cardiology</SelectItem>
                    <SelectItem value="Emergency Medicine">Emergency Medicine</SelectItem>
                    <SelectItem value="Family Medicine">Family Medicine</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="exp" className="text-stone-700">Years of Experience</Label>
                <Input data-testid="profile-experience" id="exp" type="number" min="0" max="60" value={form.experience_years} onChange={e => update('experience_years', parseInt(e.target.value) || 0)} className="mt-1" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clinic Info */}
        <Card className="border-stone-200 shadow-sm mt-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading">Clinic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="clinic" className="text-stone-700">Clinic / Hospital Name</Label>
              <Input data-testid="profile-clinic" id="clinic" value={form.clinic_name} onChange={e => update('clinic_name', e.target.value)} placeholder="City Health Clinic" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="address" className="text-stone-700">Clinic Address</Label>
              <Input data-testid="profile-address" id="address" value={form.clinic_address} onChange={e => update('clinic_address', e.target.value)} placeholder="123 Main Street, City, State - 400001" className="mt-1" />
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 flex items-center gap-3">
          <Button data-testid="save-profile-btn" type="submit" disabled={loading} className="bg-[#1A4D2E] hover:bg-[#143B23] text-white px-8 h-11 rounded-lg gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {loading ? 'Saving...' : saved ? 'Saved!' : 'Save Profile'}
          </Button>
        </div>
      </form>
    </div>
  );
}
